// note: general import
import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// note: state import
import { selectAuth, authAction } from "state/reducer/auth";

// note MUI import
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid2";
import { IconButton, InputAdornment, Link, Paper, TextField } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { MailOutline } from "@mui/icons-material";

// note: utils import
import { BimaLogoNew, TenantManagement } from "utils/asset";
import { validateEmail } from "utils/validate";

import { LazyLoadImage } from "react-lazy-load-image-component";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

function ForgotPasswordPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = React.useState("");

  const forgetPassword = useSelector(selectAuth)?.forgetPassword;

  function handleForgetPasswordSubmit(event) {
    event.preventDefault();

    dispatch(
      authAction.forgetPasswordThunk({
        pathname: "/verify/send-code",
        queryParams: `?type=forget-password`,
        body: { email },
      }),
    );
  }

  function handleBackToLogin() {
    dispatch(authAction.setClearForgetEmailInput());
    dispatch(authAction.setClearForgetPassword());
    navigate("/login");
  }

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: "100vh",
        height: "100%",
        borderRadius: 0,
        overflow: "hidden",
        fontFamily: "Manrope, sans-serif",
        p: 2,
      }}
    >
      <Grid container sx={{ height: "100vh" }}>
        {/* --- KOLOM KIRI (FORMULIR) --- */}
        <Grid
          size={{ xs: 12, md: 4.5 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#ffffff",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: { xs: 3, sm: 5, md: 6 },
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LazyLoadImage
                alt="logo"
                height="42px"
                src={BimaLogoNew}
                width="42px"
                style={{ padding: 1 }}
              />
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1a202c" }}>
                BIMA TENANT
              </Typography>
            </Box>

            {forgetPassword?.isFulfilled ? (
              <>
                <MarkEmailReadOutlinedIcon sx={{ fontSize: 48, color: "#1a202c", mt: 3 }} />
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a202c", mt: 2, mb: 1 }}>
                  Check your email
                </Typography>
                <Typography variant="body1" sx={{ color: "#718096", mb: 4 }}>
                  We've sent password reset instructions to{" "}
                  <strong>{email}</strong>. Please check your inbox.
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToLogin}
                  sx={{ width: "fit-content", textTransform: "none" }}
                >
                  Back to sign in
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a202c", mt: 3, mb: 1 }}>
                  Forgot password?
                </Typography>
                <Typography variant="body1" sx={{ color: "#718096", mb: 4 }}>
                  Enter the email address associated with your account and
                  we'll send you a link to reset your password.
                </Typography>

                <Box component="form" noValidate onSubmit={handleForgetPasswordSubmit}>
                  <Typography variant="body2" align="start" sx={{ mt: 2, color: "#718096" }}>
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    name="email"
                    placeholder="hello@hypo.studio"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailOutline sx={{ color: "#a0aec0", fontSize: "18px" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#f7fafc",
                        "& fieldset": { borderColor: "#e2e8f0" },
                      },
                    }}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!validateEmail(email) || forgetPassword?.isPending}
                    sx={{
                      mt: 3,
                      mb: 2,
                      py: 1.5,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      boxShadow: "0 4px 14px 0 rgba(9, 255, 0, 0.39)",
                      "&:hover": { bgcolor: "#a5f5c2", color: "#166534" },
                    }}
                  >
                    {forgetPassword?.isPending ? (
                      <CircularProgress size={22} sx={{ color: "#1a202c" }} />
                    ) : (
                      "Send reset link"
                    )}
                  </Button>

                  <Typography variant="body2" align="center" sx={{ color: "#718096" }}>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      sx={{ fontWeight: "bold" }}
                      onClick={handleBackToLogin}
                    >
                      Back to sign in
                    </Link>
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Grid>

        {/* --- KOLOM KANAN (PROMO) --- */}
        <Grid
          size={{ xs: 12, md: 7.5 }}
          sx={{
            position: "relative",
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 6,
            color: "#ffffff",
            height: { md: "100%" },
          }}
        >
          <DotLottieReact src={TenantManagement} loop autoplay />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ForgotPasswordPage;
